import classnames from "classnames";
import Text from "components/ui/Text";
import { useTranslation } from "react-i18next";

import { CheckCircleOutlined } from "@mui/icons-material";
import Button from "components/ui/Button";
import { PricingModel } from "services/pricingModel/model";
import { calculatePrice } from "services/pricingModel/utils";
import { formatMoney } from "utils/money";
import styles from "./style.module.scss";

type PlanContainerProps = {
  pricingModel: PricingModel;
  isSelected: boolean;
  onSelect: () => void;
};

export default function SubscriptionPlan({
  pricingModel,
  isSelected,
  onSelect,
}: PlanContainerProps) {
  const { t } = useTranslation();

  const price = pricingModel.items.map(calculatePrice).sort((a, b) => a - b)[0];

  return (
    <div className={styles.planContainer}>
      <div className={classnames(styles.planSection, styles.nameSection)}>
        <Text as="h3" variant="subtitle-1" align="left" color="var(--action-ink)">
          {pricingModel.name}
        </Text>
      </div>

      <div className={classnames(styles.planSection, styles.descriptionSection)}>
        <Text as="p" variant="body-3">
          {pricingModel.description}
        </Text>
      </div>

      <div className={classnames(styles.planSection, styles.priceSection)}>
        <Text variant="subtitle-3" className={styles.fromLabel}>
          {t("Common.comparePlans.from")}
        </Text>
        <div className={styles.priceRow}>
          <span className={styles.price}>
            {formatMoney(price, pricingModel.currency)}
          </span>
          <Text variant="subtitle-3" className={styles.subscriptionPeriod}>
            {t(
              pricingModel.type === "self-study"
                ? "Common.comparePlans.perMonth"
                : "Common.comparePlans.perSession"
            )}
          </Text>
        </div>
      </div>

      <div className={classnames(styles.planSection, styles.actionSection)}>
        <Button
          size="large"
          className={styles.selectButton}
          disabled={isSelected}
          onClick={onSelect}
        >
          {isSelected
            ? t("Common.comparePlans.yourCurrentPlan")
            : t("Common.comparePlans.choosePlan")}
        </Button>
      </div>

      {pricingModel.benefits && (
        <SubscriptionPlanBenefits
          benefits={pricingModel.benefits}
          className={classnames(styles.planSection, styles.benefitsSection)}
        />
      )}

      {pricingModel.target_audience && (
        <div className={classnames(styles.planSection, styles.targetAudience)}>
          <Text
            as="h4"
            variant="subtitle-3"
            className={styles.targetAudienceHeadline}
            align="left"
          >
            {t("Common.comparePlans.whoItsFor")}
          </Text>
          <Text as="p" variant="body-3" className={styles.audienceDescription}>
            {pricingModel.target_audience}
          </Text>
        </div>
      )}
    </div>
  );
}

type PlanBenefitsProps = {
  benefits: string;
  className?: string;
};

export function SubscriptionPlanBenefits(props: PlanBenefitsProps) {
  const { t } = useTranslation();

  const benefits = props.benefits
    .split("\n")
    .map(benefit => benefit.trim())
    .filter(Boolean);

  return (
    <div className={classnames(styles.planBenefitsContainer, props.className)}>
      <Text
        as="h4"
        variant="subtitle-3"
        className={styles.benefitsHeadline}
        align="left"
        color="var(--action-ink)"
      >
        {t("Common.comparePlans.whatsIncluded")}
      </Text>
      <ul
        className={classnames(
          styles.benefitsList,
          benefits.length > 4 && styles.benefitsListTwoColumns
        )}
      >
        {benefits.map(benefit => (
          <li key={benefit} className={styles.benefit}>
            <CheckCircleOutlined htmlColor="var(--status-success-fill)" />
            <Text variant="body-3">{benefit}</Text>
          </li>
        ))}
      </ul>
    </div>
  );
}
